import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, Validators } from '@angular/forms';

import { defer, distinctUntilChanged, map, startWith } from 'rxjs';

import type { User } from '@core/account-types';
import { filterOutUsersWithoutAccess } from '@core/candidate-data';
import { RequestEvaluationDropdownForm } from '@recruitee/evaluations-types';

@Injectable()
export class RequestEvaluationDropdownFormService {
  private readonly fb = inject(FormBuilder);

  public readonly form: RequestEvaluationDropdownForm = this.fb.group({
    offerId: new FormControl<number | null>(null),
    templateId: new FormControl<number | null>(null, Validators.required),
    admins: new FormControl<User[]>([], {
      nonNullable: true,
      validators: Validators.required,
    }),
  });

  public readonly offerId$ = defer(() =>
    this.form.controls.offerId.valueChanges.pipe(startWith(this.form.controls.offerId.value)),
  ).pipe(distinctUntilChanged());
  public readonly isValid = toSignal(
    this.form.statusChanges.pipe(map(status => status === 'VALID')),
    { initialValue: this.form.valid },
  );

  public setOfferId(offerId: number | null): void {
    this.form.controls.offerId.setValue(offerId);
  }

  public setTemplateId(templateId: number | null): void {
    this.form.controls.templateId.setValue(templateId);
  }

  public setAdmins(admins: User[]): void {
    this.form.controls.admins.setValue(admins);
  }

  public pruneAdminsByAccess(usersWithAccess: User[]): void {
    const currentAdmins = this.form.controls.admins.value;

    this.setAdmins(filterOutUsersWithoutAccess(currentAdmins, usersWithAccess));
  }
}
